/* eslint-disable prefer-object-spread */
// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { Instance as TippyInstance } from "tippy.js";

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import {
    addSearchHistory,
    getSearchHistory,
    SearchHistoryItem,
} from "@insite/client-framework/Services/AutocompleteService";
import siteMessage from "@insite/client-framework/SiteMessage";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import sendSearchTracking from "@insite/client-framework/Store/CommonHandlers/SendSearchTracking";
import setBreadcrumbs from "@insite/client-framework/Store/Components/Breadcrumbs/Handlers/SetBreadcrumbs";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductModel, UnitOfMeasureModel } from "@insite/client-framework/Types/ApiModels";
import AutocompleteContent, {
    AutocompleteContentStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteContent";
import Button, { ButtonProps } from "@insite/mobius/Button";
import { FormFieldSizeVariant } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import {
    ContentBodyProps,
    OverflowWrapperProps,
    PopoverPresentationProps,
    PositionStyle,
} from "@insite/mobius/Popover";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import FancyScroll, { FancyScrollRefProps } from "@pacific/App/Components/Common/FancyScroll";
import ProductBarcodeScanner from "@pacific/App/Components/ProductBarcodeScanner/ProductBarcodeScanner";
import { TippyPopupStyle } from "@pacific/App/Components/Tippy/Style";
import TippyPopup from "@pacific/App/Components/Tippy/TippyPopup";
import uploadImage from "@pacific/App/Handlers/ImageSearch/UploadImage";
import { xHeaderUpdate } from "@pacific/App/Handlers/XNG/Header";
import { xImageNameUpdate } from "@pacific/App/Handlers/XNG/xImageNameUpdate";
import headerSearchInputStyles from "@pacific/App/SharedStyle/HeaderSearchInputStyles";
import { CustomAppState } from "@pacific/App/Store/Reducers";
import { xIsAuthenticated } from "@pacific/App/Store/Selectors/GeneralSelectors";
import { getCustomTypeSession } from "@pacific/App/Store/Selectors/SessionSelectors";
import { getSettingsCollection } from "@pacific/App/Store/Selectors/SettingsSelectors";
import FileUpload, { FileUploadPresentationProps } from "@pacific/MobiusOverrides/FileUpload/FileUpload";
import { sizeVariantValues } from "@pacific/MobiusOverrides/FormField/formStyles";
import TextField, { TextFieldPresentationProps } from "@pacific/MobiusOverrides/TextField";
import Tooltip, { TooltipPresentationProps } from "@pacific/MobiusOverrides/Tooltip/Tooltip";
import AutocompleteBrands, { AutocompleteBrandsStyles } from "@pacific/Overrides/Widgets/Header/AutocompleteBrands";
import AutocompleteCategories, {
    AutocompleteCategoriesStyles,
} from "@pacific/Overrides/Widgets/Header/AutocompleteCategories";
import AutocompleteProducts, {
    AutocompleteProductsStyles,
} from "@pacific/Overrides/Widgets/Header/AutocompleteProducts";
import SearchHistory, { SearchHistoryStyles } from "@pacific/Overrides/Widgets/Header/SearchHistory";
import Search from "@pacific/XDL/Icons/Search";
import { SearchInputTypes, XngAutocompleteModel } from "@pacific/XDL/types";
import { color, mergeWithCss, parseBoolean, styledScrollbar, xScreen } from "@pacific/XDL/Utilities";
import { getDataLayer } from "@pacific/XDL/Utilities/dataLayer";
import produce from "immer";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import React, { createRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    /** A unique identifier to assist with id-generation for `aria-controls`. */
    readonly id: string;
    inputRef?: React.Ref<HTMLInputElement>;
    autocompletePositionFunction?: (element: React.RefObject<HTMLUListElement>) => PositionStyle;
    onBeforeGoToUrl?: () => void;
    extendedStyles?: SearchInputStyles;
    isAnimated?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const {
        searchSettings: { autocompleteEnabled, searchHistoryEnabled, searchHistoryLimit, searchPath },
        customSettings: { allowHawksearchUnifiedSearch, allowHawksearchImageSearch, allowHawksearchInstantEngage },
    } = getSettingsCollection(state);
    return {
        autocompleteEnabled,
        searchHistoryEnabled,
        searchHistoryLimit,
        location: getLocation(state),
        searchPath,
        isTabletScreen: state.xCommon.isTabletScreen,
        isMobileScreen: state.xCommon.isMobileScreen,
        isMiniScreen: state.xCommon.isMiniScreen,
        // XNG-Changes
        isAuthenticated: xIsAuthenticated(state),
        session: getCustomTypeSession(state),
        currentBillTo: getCurrentBillToState(state).value,
        allowHawksearchUnifiedSearch: parseBoolean(allowHawksearchUnifiedSearch),
        allowImageSearch: parseBoolean(allowHawksearchImageSearch),
        allowInstantEngage: parseBoolean(allowHawksearchInstantEngage),
        imageName: (state as CustomAppState).xImageName.imageNameState,
    };
};

const mapDispatchToProps = {
    getAutocompleteModel,
    sendSearchTracking,
    // XNG-Changes
    xHeaderUpdate,
    loadRealTimePricing,
    loadRealTimeInventory,
    uploadImage,
    setBreadcrumbs,
    xImageNameUpdate,
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

interface State {
    query: string;
    focusableAutocompleteItems: AutocompleteItemModel[];
    focusedAutocompleteItem?: AutocompleteItemModel;
    focusedSearchHistoryQuery?: string;
    isLoading: boolean;
    // XNG-Changes
    autoCompleteModel?: XngAutocompleteModel & { productContexts: ProductContextModel[] };
    tippyInstance?: TippyInstance;
    isPriceLoaded?: boolean;
    isInventoryLoaded?: boolean;
    imageFile?: string | null;
    hasAutocompleteResults: boolean;
    isFocused: boolean;
    lastQuery: string;
    searchType: string;
}

// XNG-Changes
const DOWN_KEY = "ArrowDown";
const UP_KEY = "ArrowUp";
const ENTER_KEY = "Enter";

export interface SearchInputStyles {
    input?: TextFieldPresentationProps;
    popover?: PopoverPresentationProps;
    popoverWrapper?: OverflowWrapperProps;
    popoverContentBody?: ContentBodyProps;
    autocompleteWrapper?: InjectableCss;
    autocompleteColumnWrapper?: InjectableCss;
    searchHistoryStyles?: SearchHistoryStyles;
    autocompleteCategoriesStyles?: AutocompleteCategoriesStyles;
    autocompleteBrandsStyles?: AutocompleteBrandsStyles;
    autocompleteContentStyles?: AutocompleteContentStyles;
    autocompleteProductsStyles?: AutocompleteProductsStyles;
    // XNG-Changes
    autocompleteColumnProductWrapper?: InjectableCss;
    inputWrapper?: InjectableCss;
    tippyPopup?: TippyPopupStyle;
    select?: SelectPresentationProps;
    wrapper?: InjectableCss;
    container?: InjectableCss;
    fileUploader?: FileUploadPresentationProps;
    searchTypeSection?: InjectableCss;
    imageSearchTooltip?: TooltipPresentationProps;
    searchButton?: ButtonProps;
    searchTooltip?: TooltipPresentationProps;
}

export const searchInputStyles: SearchInputStyles = {
    input: {
        sizeVariant: "default",
        iconProps: { src: Search, size: 16 },
        iconClickableProps: {
            component: "button",
            iconPosition: "left",
            typographyProps: {
                size: 14,
                css: css`
                    letter-spacing: 0;
                    line-height: 16px;
                    ${xScreen("lte", "md")} {
                        display: none;
                    }
                `,
            },
            css: css`
                ${IconWrapper}, svg {
                    height: 16px;
                    width: 16px;
                }
                ${xScreen("lte", "md")} {
                    padding: 8px;
                    ${IconWrapper} {
                        margin-right: 0;
                    }
                }
            `,
        },
        cssOverrides: {
            inputSelect: css`
                height: 44px;
            `,
        },
    },
    popover: {
        transitionDuration: "regular",
        toggle: false,
    },
    popoverWrapper: {
        as: "div",
        _height: "auto",
        css: css`
            display: inline-flex;
            justify-content: flex-end;
        `,
    },
    popoverContentBody: {
        as: "div",
        _height: "100%",
    },
    autocompleteWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            ${xScreen("lte", "md")} {
                flex-direction: column;
            }
        `,
    },
    autocompleteColumnWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 400px;
            min-width: 400px;
            min-height: 400px;
            /* XNGAGE Changes: remove max-height: 420px; */
            padding-bottom: 20px;
            position: relative;
            &:after {
                content: "";
                background: ${color("common.ui4")};
                width: 1px;
                height: 100%;
                position: absolute;
                right: 0;
                top: 0;
            }
            ${xScreen("lte", "md")} {
                width: 100%;
                min-width: initial;
                min-height: unset;
                padding-bottom: 4px;
                &:after {
                    display: none;
                }
            }
            ${xScreen("gt", "md")} {
                ${styledScrollbar(4)}
            }
        `,
    },
    autocompleteColumnProductWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 560px;
            padding: 16px 0 0 16px;
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
    autocompleteCategoriesStyles: {
        headerText: {
            ...headerSearchInputStyles?.headerText,
        },
        link: {
            ...headerSearchInputStyles?.link,
        },
        focusedCategoryItemText: {
            ...headerSearchInputStyles?.focusedItemText,
        },
        focusedLink: {
            ...headerSearchInputStyles?.focusedLink,
        },
        categoryItemText: {
            ...headerSearchInputStyles?.itemText,
        },
        itemWrapper: {
            ...headerSearchInputStyles?.itemWrapper,
        },
    },
    autocompleteBrandsStyles: {
        headerText: {
            ...headerSearchInputStyles?.headerText,
        },
        link: {
            ...headerSearchInputStyles?.link,
        },
        focusedBrandItemText: {
            ...headerSearchInputStyles?.focusedItemText,
        },
        focusedLink: {
            ...headerSearchInputStyles?.focusedLink,
        },
        brandItemText: {
            ...headerSearchInputStyles?.itemText,
        },
        itemWrapper: {
            ...headerSearchInputStyles?.itemWrapper,
        },
    },
    autocompleteContentStyles: {
        headerText: {
            ...headerSearchInputStyles?.headerText,
        },
        link: {
            ...mergeWithCss(headerSearchInputStyles?.link, headerSearchInputStyles?.itemWrapper),
        },
        focusedLink: {
            ...mergeWithCss(headerSearchInputStyles?.focusedLink, headerSearchInputStyles?.itemWrapper),
        },
    },
    inputWrapper: {
        css: css`
            line-height: 0; // to fix extra spacing on firefox
            position: relative;
        `,
    },
};

const styles = searchInputStyles;

// XNG-Changes: DesktopTooltip component
const DesktopTooltip = ({ isTabletScreen, isMobileScreen, triggerComponent, ...tooltipProps }: any) => {
    if (isTabletScreen || isMobileScreen) {
        return triggerComponent;
    }

    return <Tooltip {...tooltipProps} triggerComponent={triggerComponent} />;
};

class SearchInput extends React.Component<Props, State> {
    private readonly styles: SearchInputStyles;

    // XNG-Changes
    private parentMenu = React.createRef<HTMLDivElement>();
    private inputWrapperRef = createRef<HTMLDivElement>();
    private fancyScrollRef = createRef<FancyScrollRefProps>();

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);

        this.state = {
            query: "",
            focusableAutocompleteItems: [],
            isLoading: false,
            tippyInstance: undefined,
            isPriceLoaded: false,
            isInventoryLoaded: false,
            // XNG-Changes: hawk search
            imageFile: null,
            hasAutocompleteResults: false,
            isFocused: false,
            lastQuery: "",
            searchType: "",
        };
    }

    list = React.createRef<HTMLUListElement>();
    popover = React.createRef<any>();
    showAutocomplete = true;

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.location.pathname !== prevProps.location.pathname) {
            // clear the search box on navigate away from search
            if (!this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // TODO ISC-12679 - Redesign so that this can be moved to a field of the class rather than state.
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({ query: "" });

                // <XNG-Changes>
                // OOTB issue, when a user do search and return back to home page, the history not showAutocomplete update so the history popup not appear
                this.showAutocomplete = true;
                // </XNG-Changes>
            } else {
                this.closeAutocomplete();
            }
        }
        if (
            this.state.autoCompleteModel &&
            prevState.autoCompleteModel &&
            this.state.autoCompleteModel !== prevState.autoCompleteModel
        ) {
            this.updateFocusableItems();
        }
    }

    componentDidMount() {
        window.addEventListener("resize", this.handleResize, true);
        // XNG-Changes: hawk search
        const parsedQuery = parseQueryString<{ searchRequestType: string }>(
            this.props.location.search,
        ).searchRequestType;
        if (parsedQuery) {
            this.setState({ searchType: parsedQuery });
        }
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
    }

    handleResize = () => {
        this.updateFocusableItems();
    };

    updateFocusableItems = () => {
        if (this.state.isLoading || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const isProductsOnTop = window.innerWidth < this.props.theme.breakpoints.values[3];
        const newFocusableOptions = isProductsOnTop
            ? (products || ([] as AutocompleteItemModel[]))
                  .concat(categories || [])
                  .concat(brands || [])
                  .concat(content || [])
            : (categories || [])
                  .concat(brands || [])
                  .concat(content || [])
                  .concat(products || []);
        this.setState({
            focusableAutocompleteItems: newFocusableOptions,
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    // XNG-Changes:hawk search update function to handle uploading image and concept search
    doSearch = () => {
        const { imageFile, searchType } = this.state;
        let searchRequestType;
        if (searchType?.toLowerCase() === SearchInputTypes.keyword) {
            searchRequestType = SearchInputTypes.keyword;
        } else if (this.props.allowHawksearchUnifiedSearch) {
            searchRequestType = SearchInputTypes.unifiedSearch;
        }
        if (searchType === SearchInputTypes.imageSearch) {
            if (!imageFile) {
                return;
            }
            this.setState({ query: "" });
            this.props.setBreadcrumbs({
                links: [
                    { children: translate("Home_Breadcrumb"), href: "/" },
                    { children: `${translate("Search Results")}: Image` },
                ],
            });
            const that = this;
            this.props.uploadImage({
                imageData: imageFile,
                onSuccess: res => {
                    searchRequestType = SearchInputTypes.imageSearch;
                    that.goToUrl(
                        `/${this.props.searchPath}?query=&searchRequestType=${searchRequestType}&searchImageKey=${res.key}`,
                    );
                },
            });
        } else {
            if (this.state.query.trim() === "") {
                return;
            }

            this.showAutocomplete = false;
            if (this.state.autoCompleteModel?.products?.length === 1) {
                this.goToUrl(this.state.autoCompleteModel?.products[0].url);
            } else {
                const encodedQuery = encodeURIComponent(this.state.query);
                if (searchType === SearchInputTypes.keyword || !this.props.allowHawksearchUnifiedSearch) {
                    this.goToUrl(`/${this.props.searchPath}?query=${encodedQuery}`, false);
                } else {
                    this.goToUrl(
                        `/${this.props.searchPath}?query=${encodedQuery}&searchRequestType=${searchRequestType}`,
                    );
                }
            }
            if (this.props.searchHistoryEnabled) {
                addSearchHistory(this.state.query, this.props.searchHistoryLimit);
            }
        }
    };

    // XNG-Changes: fix empty popup
    clearSearchQuery = () => {
        this.showAutocomplete = true;
        this.setState({ query: "" });
    };

    // XNG-Changes: hawk search handle image change
    handleImageUpload = (event: React.ChangeEvent<any>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onloadend = () => {
                const result = reader.result;
                if (typeof result === "string") {
                    const base64String = result.split(",")[1];
                    this.setState({ imageFile: base64String, searchType: SearchInputTypes.imageSearch }, () => {
                        this.doSearch();
                    });
                    this.props.xImageNameUpdate({ imageNameState: file.name });
                    // Reset the file input so the same file can be selected again
                    if (event.target) {
                        event.target.value = "";
                    }
                }
            };
        }
    };

    loadAutocomplete = debounce((query: string) => {
        this.setState({ isPriceLoaded: false, isInventoryLoaded: false, lastQuery: query });

        this.props.getAutocompleteModel({
            query,
            onSuccess: (result: XngAutocompleteModel) => {
                if (this.showAutocomplete) {
                    this.setState(
                        {
                            autoCompleteModel: {
                                ...result,
                                productContexts: (result?.products || []).map(product => ({
                                    product: {
                                        id: product.id!,
                                        productNumber: product.erpNumber,
                                        mediumImagePath: product.image,
                                        smallImagePath: product.image,
                                        largeImagePath: product.image,
                                        productTitle: product.title,
                                        canonicalUrl: product.url,
                                        uri: product.uri,
                                        canAddToCart: product.canAddToCart,
                                        cantBuy: product.cantBuy,
                                        canConfigure: product.canConfigure,
                                        manufacturerItem: product.manufacturerItemNumber,
                                        minimumOrderQty: product.minimumOrderQty,
                                        brand: {
                                            name: product.brandName,
                                            detailPagePath: product.brandDetailPagePath,
                                        },
                                        customerProductNumber: product.isNameCustomerOverride ? product.name : "",
                                        trackInventory: product.trackInventory,
                                        properties: {
                                            ...product.properties,
                                        },
                                        detail: {
                                            upcCode: product.upcCode,
                                        },
                                        quoteRequired: product.isQuoteRequired,
                                        ...(product as any),
                                    } as ProductModel,
                                    productInfo: {
                                        productId: product.id!,
                                        productDetailPath: product.url,
                                        qtyOrdered: parseInt(product.properties.minimumOrderQty || "1", 10),
                                        unitOfMeasure: "",
                                    },
                                    onQtyOrderedChanged: (qty: number) => {
                                        const autoCompleteModel = produce(
                                            this.state.autoCompleteModel,
                                            draftedState => {
                                                if (!draftedState) {
                                                    return;
                                                }
                                                const productIndex = draftedState.productContexts.findIndex(
                                                    p => p.product.id === product.id,
                                                );
                                                draftedState.productContexts[productIndex].productInfo.qtyOrdered = qty;
                                            },
                                        );
                                        this.setState({ autoCompleteModel });
                                    },
                                })),
                            },
                        },
                        () => {
                            this.fancyScrollRef?.current?.updateScrollable();
                        },
                    );
                    // XNG-Changes
                    if (result?.products?.length) {
                        const tempProducts = result?.products;
                        this.props.loadRealTimePricing({
                            productPriceParameters: (tempProducts || [])!.map(product => ({
                                productId: product?.id as string,
                                unitOfMeasure: (product as any)?.unitOfMeasures?.find(
                                    (u: UnitOfMeasureModel) => u.isDefault,
                                )?.unitOfMeasure as string,
                                qtyOrdered: 1,
                                productDetailPath: product.url,
                            })),
                            additionalQueryStringParameters: { RetailPricePage: true },
                            onComplete: realTimePricingProps => {
                                if (realTimePricingProps?.apiResult) {
                                    realTimePricingProps.apiResult.realTimePricingResults?.forEach(pricing => {
                                        tempProducts?.forEach(product => {
                                            if (
                                                product.id === pricing.productId &&
                                                (product as any).unitOfMeasures.find(
                                                    (u: UnitOfMeasureModel) => u.isDefault,
                                                ).unitOfMeasure === pricing.unitOfMeasure
                                            ) {
                                                (product as any).pricing = pricing;
                                            }
                                        });
                                    });
                                    const autoCompleteModel = this.state.autoCompleteModel;
                                    autoCompleteModel!.products = tempProducts;
                                    autoCompleteModel?.productContexts?.forEach(px => {
                                        (px.productInfo as any).pricing = (
                                            tempProducts.find(p => p.id === px.product.id) as any
                                        )?.pricing;
                                        px.productInfo.unitOfMeasure = (
                                            tempProducts.find(p => p.id === px.product.id) as any
                                        )?.unitOfMeasures.find((u: UnitOfMeasureModel) => u.isDefault).unitOfMeasure;
                                        px.product.unitOfMeasures = (
                                            tempProducts.find(p => p.id === px.product.id) as any
                                        )?.unitOfMeasures;
                                    });
                                    this.setState({ autoCompleteModel, isPriceLoaded: true });
                                    this.props.loadRealTimeInventory({
                                        productIds: tempProducts.map(o => o.id) as string[],
                                        onSuccess: realTimeInventory => {
                                            if (realTimeInventory.realTimeInventoryResults) {
                                                realTimeInventory.realTimeInventoryResults?.forEach(inventory => {
                                                    tempProducts?.forEach(product => {
                                                        if (product.id === inventory.productId) {
                                                            (product as any).inventory = inventory;
                                                        }
                                                    });
                                                });
                                                const autoCompleteModel = Object.assign(
                                                    {},
                                                    this.state.autoCompleteModel,
                                                );
                                                autoCompleteModel!.products = tempProducts;
                                                autoCompleteModel &&
                                                    autoCompleteModel.productContexts &&
                                                    autoCompleteModel.productContexts.forEach(px => {
                                                        (px.productInfo as any).inventory = (
                                                            tempProducts.find(p => p.id === px.product.id) as any
                                                        )?.inventory;
                                                    });
                                                this.setState({ autoCompleteModel, isInventoryLoaded: true });
                                                this.renderAutocomplete();
                                            }
                                        },
                                        onError: () => {
                                            tempProducts?.forEach(product => {
                                                (product as any).failedToLoadInventory = true;
                                            });
                                        },
                                        onComplete(realTimeInventoryProps) {
                                            if (realTimeInventoryProps.apiResult) {
                                                this.onSuccess?.(realTimeInventoryProps.apiResult);
                                            } else if (realTimeInventoryProps.error) {
                                                this.onError?.(realTimeInventoryProps.error);
                                            }
                                        },
                                    });
                                }
                            },
                        });
                    }
                    // <XNG-Changes>
                    if (this.state.isFocused) {
                        this.updatePopoverVisibility(result);
                    }
                    // </XNG-Changes>
                    // this.openAutocomplete();
                }
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        });
    }, 500);

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, toggleAutoComplete = true) => {
        this.showAutocomplete = toggleAutoComplete;
        const newQuery = event.target.value;
        // XNG-Changes: Reset imageFile
        this.setState({
            query: newQuery,
            imageFile: null,
            searchType: this.props.allowHawksearchUnifiedSearch
                ? SearchInputTypes.unifiedSearch
                : SearchInputTypes.keyword,
        });
        this.loadAutocomplete(newQuery);

        // XNG-Changes
        this.updatePopoverVisibility(this.state.autoCompleteModel);
    };

    openAutocomplete = () => {
        // XNG-Changes
        this.state.tippyInstance?.show();
    };

    closeAutocomplete = () => {
        // XNG-Changes
        this.state.tippyInstance?.hide();
    };

    // XNG-Changes: Check if popeover is opened
    isPopoverVisible(results: XngAutocompleteModel | undefined) {
        let hasResults: boolean | null = false;
        if (results) {
            const { categories, brands, content, products } = results;
            hasResults =
                this.props.autocompleteEnabled &&
                ((categories && categories.length > 0) ||
                    (brands && brands.length > 0) ||
                    (content && content.length > 0) ||
                    (products && products.length > 0));
        }

        const hasSearchHistory = this.props.searchHistoryEnabled && getSearchHistory()?.length > 0;

        return hasResults || hasSearchHistory;
    }

    // XNG-Changes
    updatePopoverVisibility(results: XngAutocompleteModel | undefined) {
        let hasResults: boolean | null = false;
        if (results) {
            const { categories, brands, content, products } = results;
            hasResults =
                this.props.autocompleteEnabled &&
                ((categories && categories.length > 0) ||
                    (brands && brands.length > 0) ||
                    (content && content.length > 0) ||
                    (products && products.length > 0));
        }

        this.setState({ hasAutocompleteResults: !!hasResults });

        if (this.isPopoverVisible(results)) {
            this.openAutocomplete();
        } else {
            this.closeAutocomplete();
        }
    }

    // XNG-Changes
    handleInputFocus = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.updatePopoverVisibility(this.state.autoCompleteModel);
        const newQuery = event.target.value;
        this.setState({ query: newQuery });
        this.setState({ isFocused: true });
        if (!newQuery && this.props.allowInstantEngage) {
            // XNG-Change
            // PPSC-1314 call search request if a user focused on the input and query is ""
            this.setState(
                {
                    searchType: this.props.allowHawksearchUnifiedSearch
                        ? SearchInputTypes.unifiedSearch
                        : SearchInputTypes.keyword,
                },
                () => this.doSearch(),
            );

            this.loadAutocomplete(newQuery);
        }
        // XNG-Chages: show auto complete in case of searching in concept and returning to keyword
        if (this.state.query !== this.state.lastQuery) {
            this.loadAutocomplete(newQuery);
        }
    };

    // XNG-Changes: PPSC: 1314
    handleBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ isFocused: false });
    };

    // XNG-Changes
    clearSearchHistoryCallback = () => {
        this.updatePopoverVisibility(undefined);
    };

    getPopoverPosition = (element: React.RefObject<HTMLUListElement>) => {
        if (this.props.autocompletePositionFunction) {
            return this.props.autocompletePositionFunction(element);
        }

        const positionStyle: PositionStyle = {
            position: "fixed",
        };

        if (element.current) {
            const rect = element.current.getBoundingClientRect();
            positionStyle.top =
                rect.top +
                (rect.height > 100
                    ? sizeVariantValues[this.styles?.input?.sizeVariant || ("default" as FormFieldSizeVariant)].height
                    : rect.height);
            positionStyle.right = document.documentElement.clientWidth - rect.right;

            if (this.props.isAnimated) {
                // Adjust for compact header usage
                positionStyle.top += 3;
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderRight) {
                    positionStyle.right -= Number(this.props.theme.header.borderThickness);
                }
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderTop) {
                    positionStyle.top -= Number(this.props.theme.header.borderThickness);
                }
            }
        }

        return positionStyle;
    };

    moveFocus = throttle((direction: "prev" | "next") => {
        if (this.state.query) {
            this.setState(({ focusedAutocompleteItem, focusableAutocompleteItems }) => {
                const focusableQueue =
                    direction === "prev" ? focusableAutocompleteItems.slice().reverse() : focusableAutocompleteItems;
                const focusedIndex = focusableQueue.indexOf(focusedAutocompleteItem as AutocompleteItemModel);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedAutocompleteItem: newFocusedOption };
            });
        } else {
            this.setState(({ focusedSearchHistoryQuery }) => {
                const searchHistoryItems = getSearchHistory();
                const focusableQueue = direction === "prev" ? searchHistoryItems.slice().reverse() : searchHistoryItems;
                const focusedSearchHistoryItem = focusableQueue.find(o => o.query === focusedSearchHistoryQuery);
                const focusedIndex = focusableQueue.indexOf(focusedSearchHistoryItem as SearchHistoryItem);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedSearchHistoryQuery: newFocusedOption?.query };
            });
        }
    }, 100);

    handlePopoverKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        switch (event.key) {
            case DOWN_KEY:
                this.moveFocus("next");
                break;
            case UP_KEY:
                this.moveFocus("prev");
                break;
            case ENTER_KEY:
                if (this.state.focusedAutocompleteItem) {
                    // XNG-Changes
                    event.currentTarget.blur();
                    this.goToUrl(this.state.focusedAutocompleteItem.url);
                } else if (this.state.focusedSearchHistoryQuery) {
                    // XNG-Changes
                    event.currentTarget.blur();
                    this.setState({ query: this.state.focusedSearchHistoryQuery }, () => {
                        this.doSearch();
                    });
                } else {
                    // XNG-Changes
                    event.currentTarget.blur();
                    this.doSearch();
                }
                break;
            default:
                break;
        }
    };

    handlePopoverClose = () => {
        this.setState({
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    trackAutcompleteSearchResults = () => {
        // Make sure only to trigger for autocomplete results
        if (!this.state.query || !this.props.autocompleteEnabled || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const resultCount =
            (products?.length ?? 0) + (categories?.length || 0) + (content?.length || 0) + (brands?.length || 0);
        if (resultCount > 0) {
            // Track an autocomplete of 1+ items
            this.props.sendSearchTracking({
                searchEvent: "autocompleteSearchResults",
                searchTerm: this.state.query,
                resultCount,
                productCount: products?.length,
                categoryCount: categories?.length,
                contentCount: content?.length,
                brandCount: brands?.length,
            });
            return;
        }

        // Track zero resultCount search results and triggers from this.doSearch
        this.props.sendSearchTracking({
            searchEvent: "searchResults",
            searchTerm: this.state.query,
            resultCount,
        });
    };

    goToUrl = (url: string, track = true) => {
        this.props.onBeforeGoToUrl && this.props.onBeforeGoToUrl();
        if (track) {
            this.trackAutcompleteSearchResults();
        }
        this.closeAutocomplete();
        this.setState({
            // don't use stale results on next search and go to wrong PDP
            autoCompleteModel: undefined,
        });
        this.props.history.push(url);
    };

    renderAutocomplete = () => {
        if (!this.props.autocompleteEnabled) {
            return null;
        }

        // XNG-Chages: hide auto complete if imageSearch
        if (this.state.searchType === SearchInputTypes.imageSearch) {
            return null;
        }

        if (!this.state.autoCompleteModel) {
            return null;
        }

        const { categories, brands, content, productContexts } = this.state.autoCompleteModel;
        // XNG-Changes: GA4 AutoComplete Event
        const _GaAutoCompleteEvent = (item: string, destination: string) => {
            const dataLayer = getDataLayer();
            if (!dataLayer) {
                return;
            }
            const { currentBillTo, session, isAuthenticated } = this.props;
            const googleAnalyticsData = {
                event: "autocomplete_search_results",
                item_clicked: item,
                destination,
                authentication_state: isAuthenticated ? "Authenticated" : "Guest",
                affiliation: currentBillTo?.companyName || "",
                sales_rep: (session as any)?.salesperson?.name || "",
            };
            dataLayer.push(googleAnalyticsData);
        };

        return (
            <StyledWrapper {...this.styles.autocompleteWrapper} className="autocomplete-wrapper">
                {((categories && categories.length > 0) ||
                    (brands && brands.length > 0) ||
                    (content && content.length > 0)) && (
                    <StyledWrapper {...this.styles.autocompleteColumnWrapper} className="autocomplete-column-wrapper">
                        <AutocompleteCategories
                            categories={categories}
                            focusedItem={this.state.focusedAutocompleteItem}
                            goToUrl={this.goToUrl}
                            extendedStyles={this.styles.autocompleteCategoriesStyles}
                            onClickEvent={_GaAutoCompleteEvent}
                        />
                        <AutocompleteBrands
                            brands={brands}
                            focusedItem={this.state.focusedAutocompleteItem}
                            goToUrl={this.goToUrl}
                            extendedStyles={this.styles.autocompleteBrandsStyles}
                            onClickEvent={_GaAutoCompleteEvent}
                        />
                        <AutocompleteContent
                            content={content}
                            focusedItem={this.state.focusedAutocompleteItem}
                            goToUrl={this.goToUrl}
                            extendedStyles={this.styles.autocompleteContentStyles}
                        />
                    </StyledWrapper>
                )}
                {productContexts && productContexts.length > 0 && (
                    <FancyScroll refElement={this.parentMenu} ref={this.fancyScrollRef}>
                        <StyledWrapper
                            {...this.styles.autocompleteColumnProductWrapper}
                            className="autocomplete-column-wrapper"
                            ref={this.parentMenu}
                        >
                            <AutocompleteProducts
                                query={this.state.query}
                                products={productContexts}
                                focusedItem={this.state.focusedAutocompleteItem}
                                goToUrl={this.goToUrl}
                                doSearch={this.doSearch}
                                extendedStyles={this.styles.autocompleteProductsStyles}
                                isPriceLoaded={this.state.isPriceLoaded}
                                isInventoryLoaded={this.state.isInventoryLoaded}
                                onClickEvent={_GaAutoCompleteEvent}
                            />
                        </StyledWrapper>
                    </FancyScroll>
                )}
            </StyledWrapper>
        );
    };

    // XNG-Changes
    renderSearchAutoCompleteContent = () => {
        const { query, hasAutocompleteResults } = this.state;

        if (hasAutocompleteResults) {
            return this.renderAutocomplete();
        }
        if (!query && this.showAutocomplete) {
            return (
                <SearchHistory
                    focusedQuery={this.state.focusedSearchHistoryQuery}
                    goToUrl={this.goToUrl}
                    extendedStyles={styles.searchHistoryStyles}
                    width={this.inputWrapperRef.current?.getBoundingClientRect().width || 300}
                />
            );
        }
        return null;
    };

    render() {
        const styles = this.styles;
        const searchId = `headerSearch-${this.props.id}`;
        const { xHeaderUpdate, isTabletScreen, isMobileScreen, isMiniScreen, allowImageSearch } = this.props;
        // XNG-Changes: hawk search
        const { imageName } = this.props;

        const searchInput = (
            <StyledWrapper ref={this.inputWrapperRef} {...styles.inputWrapper}>
                <TextField
                    {...styles.input}
                    ref={this.props.inputRef}
                    isAnimated={this.props.isAnimated}
                    // XNG-Changes: remove iconClickableProps
                    // iconClickableProps={{ ...styles.input?.iconClickableProps, onClick: this.doSearch }}
                    value={this.state.query}
                    onChange={this.handleInputChange}
                    placeholder={translate("Search by Keyword")}
                    autoComplete="off"
                    clickableText={translate("Search")}
                    id={searchId}
                    data-test-selector="headerSearchInputTextField"
                    className="search-input"
                    // XNG-Changes
                    onKeyDown={this.handlePopoverKeyDown}
                    aria-multiline="false"
                    aria-required="false"
                    onFocus={this.handleInputFocus}
                    onBlur={this.handleBlur}
                    maxLength={100}
                />
                {/* XNG-Changes: PPSC-1343:update header search box */}
                <Hidden above="md">
                    <ProductBarcodeScanner />
                </Hidden>

                <DesktopTooltip
                    isTabletScreen={isTabletScreen}
                    isMobileScreen={isMobileScreen}
                    text={siteMessage("Custom_Search_info")}
                    {...styles.searchTooltip}
                    triggerComponent={
                        <Button
                            {...styles.searchButton}
                            onMouseDown={this.doSearch}
                            aria-label={translate("Search")}
                            role="search"
                            data-test-selector="submitSearch"
                        ></Button>
                    }
                />

                {allowImageSearch && (
                    <DesktopTooltip
                        isTabletScreen={isTabletScreen}
                        isMobileScreen={isMobileScreen}
                        text={siteMessage("Custom_Search_image_info")}
                        {...styles.imageSearchTooltip}
                        triggerComponent={
                            <FileUpload
                                {...styles.fileUploader}
                                fileName={this.state.imageFile || ""}
                                accept="image/*"
                                onFileChange={this.handleImageUpload}
                                uid="image-upload"
                                hideInput={true}
                                buttonText={translate("Upload")}
                            />
                        }
                    />
                )}
            </StyledWrapper>
        );

        return (
            <StyledWrapper {...styles.container}>
                <VisuallyHidden as="label" htmlFor={searchId} id={`${searchId}-label`}>
                    {translate("Site Search")}
                </VisuallyHidden>

                {/* XNG-Changes: change popover with tippy */}
                <TippyPopup
                    onCreate={instance => {
                        this.setState({ tippyInstance: instance });
                    }}
                    onShow={instance => {
                        xHeaderUpdate({ key: "registerMenu", value: `${instance.id}_search` });
                        this.fancyScrollRef?.current?.updateScrollable();
                        this.setState({ tippyInstance: instance });
                    }}
                    onHidden={instance => {
                        xHeaderUpdate({ key: "unregisterMenu", value: `${instance.id}_search` });
                    }}
                    content={<>{this.renderSearchAutoCompleteContent()}</>}
                    trigger="manual"
                    offset={isTabletScreen && !isMobileScreen ? [0, 16] : [0, 0]}
                    duration={[null, null]}
                    arrow={false}
                    maxWidth="none"
                    placement="bottom-start"
                    type="menu"
                    mobileMode={isMiniScreen ? "left-side" : "default"}
                    showCloseButton={false}
                    appendTo={() => document.body}
                    extendedStyles={styles.tippyPopup}
                    popperOptions={{
                        strategy: isMobileScreen ? "fixed" : "absolute",
                        modifiers: [
                            {
                                name: "preventOverflow",
                                options: {
                                    padding: 0,
                                },
                            },
                            {
                                name: "computeStyles",
                                options: {
                                    adaptive: !isMobileScreen, // false for mobile only
                                    roundOffsets: ({ x, y }: any) => ({
                                        x,
                                        y: isMiniScreen ? 0 : y,
                                    }),
                                },
                            },
                        ],
                    }}
                >
                    {searchInput}
                </TippyPopup>
            </StyledWrapper>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(SearchInput)));
